import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios'
import { LocalStorageKey } from 'types/services/localStorage'
import { getAccessToken } from 'utils/auth'
import LocalStorage from './LocalStorage'
import Logger from 'services/Logger'
import { login, LOGIN_URL } from './api/auth'
import axiosRetry from 'axios-retry'
import { getOrUpdateDeviceId } from 'utils/device'
import dayjs from 'dayjs'
import { API_CHANGES } from './api/changes'
import {
  ADD_EVENT_CANNOT_POST_ITEMS,
  ADD_EVENT_NOT_PART_OF_GROUP,
  EDIT_EVENT_CAN_CHANGE_ONLY_OWNERS,
  EDIT_EVENT_CANNOT_CHANGE_EVENT,
  EDIT_EVENT_NOT_PART_OF_GROUP,
  EDIT_GROUP_ADD_PARTICIPANTS_USER_NOT_ADMIN,
  EDIT_GROUP_METADATA_USER_NOT_AUTHORIZED,
  EDIT_GROUP_SETTINGS_USER_NOT_AUTHORIZED,
  EDIT_GROUP_USER_NOT_PART_OF_GROUP,
  EDIT_PARTICIPANTS_USER_NOT_AUTHORIZED,
  OUT_OF_TIER
} from 'utils/api/codes'
import { isTestEnv } from 'utils/env'

export function API_URL() {
  const picked = LocalStorage.get(LocalStorageKey.APP_HOST)

  const apiUrl =
    isTestEnv() && picked !== null && picked !== undefined
      ? picked
      : process.env.REACT_APP_API_URL

  return apiUrl
}

/**
 * Determines if a request should be retried based on the error
 */
function shouldRetry(error: AxiosError) {
  const httpCode = error.response?.status

  if (httpCode === 403) {
    /**
     * we have specific cases when server return 403, but we should not retry the request, such as:
     * out of tier, user blocked from adding/editing events etc
     */
    const code = (error.response?.data as any)?.code

    if (VALID_403_CODES.includes(code)) {
      return false
    }
  }

  return (
    httpCode === 403 && LocalStorage.get(LocalStorageKey.USER_ID) != undefined
  )
}

class Api {
  instance = axios.create({
    baseURL: `${API_URL()}/api`
  })

  constructor() {
    // Updated request interceptor for Axios 1.x
    this.instance.interceptors.request.use(
      (req: InternalAxiosRequestConfig) => {
        if (getAccessToken()) {
          const token = getAccessToken() as string
          const skipAuth = req.url?.includes(LOGIN_URL)

          if (!skipAuth) {
            req.headers.set('Authorization', token)
          }
        }
        return req
      }
    )

    // Updated response interceptor for Axios 1.x
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        if (!shouldRetry(error)) {
          return Promise.reject(error)
        }

        // Make sure config exists and is typed correctly
        const originalRequest = error.config
        if (!originalRequest) {
          return Promise.reject(error)
        }

        // Delay original requests until authorization has been completed
        try {
          await login()

          // Create new request with updated token
          const token = getAccessToken() as string

          // Set the Authorization header properly for Axios 1.x
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = token
          }

          return axios.request(originalRequest)
        } catch (loginError) {
          return Promise.reject(loginError)
        }
      }
    )

    // Configure axios-retry
    axiosRetry(this.instance, {
      retries: 3,
      retryCondition: (error) => {
        return shouldRetry(error)
      },
      retryDelay: (retryCount) => {
        return retryCount * 5000
      }
    })
  }

  public get(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> {
    Logger.blue(`request to ${url}`)
    return this.instance.get(url, config)
  }

  public delete(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    Logger.blue(`request to ${url}`)
    return this.instance.delete(url, config)
  }

  public post(
    url: string,
    body: Record<string, string | number | boolean | object | undefined | null>,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    const lastUpdate = LocalStorage.get(LocalStorageKey.LAST_UPDATE)

    const postBody = {
      ...body
    }

    if (lastUpdate && body['LastUpdate'] != null) {
      // If we have last update, meaning very first getchanges finished
      Object.assign(postBody, {
        DeviceChangeID: getOrUpdateDeviceId(),
        LastUpdate: url.includes(API_CHANGES)
          ? lastUpdate
          : `${dayjs().unix()}`,
        UserID: LocalStorage.get(LocalStorageKey.USER_ID)
      })
    }

    Logger.blue(`request to ${url}; with body`, postBody)
    return this.instance.post(url, postBody, config)
  }
}

export default new Api()

export const VALID_403_CODES = [
  EDIT_PARTICIPANTS_USER_NOT_AUTHORIZED,
  EDIT_GROUP_METADATA_USER_NOT_AUTHORIZED,
  EDIT_GROUP_SETTINGS_USER_NOT_AUTHORIZED,
  EDIT_GROUP_USER_NOT_PART_OF_GROUP,
  EDIT_GROUP_ADD_PARTICIPANTS_USER_NOT_ADMIN,
  OUT_OF_TIER,
  ADD_EVENT_NOT_PART_OF_GROUP,
  ADD_EVENT_CANNOT_POST_ITEMS,
  EDIT_EVENT_NOT_PART_OF_GROUP,
  EDIT_EVENT_CANNOT_CHANGE_EVENT,
  EDIT_EVENT_CAN_CHANGE_ONLY_OWNERS
]
